import { assertCatalogRowGoogleLiveReferenceSafe } from './googleLiveReferencePolicy.js';

export const DEFAULT_PERCENTILE = 10;
export const DEFAULT_BATCH_SIZE = 50;

const IMPORT_TABLES = Object.freeze(['places', 'businesses']);

function normalizePercentile(value, fallback = DEFAULT_PERCENTILE) {
  if (value == null || value === '') return fallback;
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0 && numeric <= 100) return numeric;
  return fallback;
}

function resolvePercentile(scoredData, options = {}) {
  const metadataPercentile = scoredData && scoredData.metadata
    ? scoredData.metadata.percentile
    : null;
  return normalizePercentile(options.percentile, normalizePercentile(metadataPercentile));
}

function normalizeBatchSize(value) {
  const numeric = Number(value);
  if (Number.isInteger(numeric) && numeric > 0) return numeric;
  return DEFAULT_BATCH_SIZE;
}

function normalizePlaceId(value) {
  return String(value || '').trim();
}

function normalizePlaceIdFilter(options = {}) {
  const rawIds = Array.isArray(options.placeIds)
    ? options.placeIds
    : options.placeIdAllowlist;

  if (!Array.isArray(rawIds)) return null;

  const ids = rawIds.map(normalizePlaceId).filter(Boolean);
  if (ids.length === 0) return null;

  return Array.from(new Set(ids));
}

function getScoredPlaces(scoredData) {
  if (!scoredData || typeof scoredData !== 'object') {
    throw new Error('Invalid scored data: expected an object with a places array');
  }
  if (!Array.isArray(scoredData.places)) {
    throw new Error('Invalid scored data: places must be an array');
  }
  return scoredData.places;
}

export function selectTopScoredPlaces(scoredData, options = {}) {
  const places = getScoredPlaces(scoredData);
  const percentile = resolvePercentile(scoredData, options);
  const topCount = places.length > 0
    ? Math.max(1, Math.ceil((places.length * percentile) / 100))
    : 0;

  return {
    percentile,
    topCount,
    places: places.slice(0, topCount),
    totalCount: places.length,
  };
}

function requireImportRules(rules) {
  const missing = [];
  if (!rules || typeof rules.getCategoryTable !== 'function') missing.push('getCategoryTable');
  if (!rules || !rules.categoryTables) missing.push('categoryTables');
  if (missing.length > 0) {
    throw new Error(`Missing import planner rule adapters: ${missing.join(', ')}`);
  }
}

export function createImportRuleAdapter(domainRules, adapters = {}) {
  return {
    categoryTables: domainRules.CATEGORY_TABLES,
    getCategoryTable: domainRules.getCategoryTable,
    classifyBoundaryCleanupAction: adapters.classifyBoundaryCleanupAction || domainRules.classifyBoundaryCleanupAction || null,
    findKnownVillageCaza: domainRules.findKnownVillageCaza || null,
    isCoordinateInCatalogCaza: domainRules.isCoordinateInCatalogCaza || null,
    normalizeCatalogCaza: domainRules.normalizeCatalogCaza || null,
    photoUrlForReference: adapters.photoUrlForReference || null,
  };
}

function normalizeName(value) {
  return String(value || '').trim().toLowerCase();
}

export function manualPlaceId(name) {
  return `manual_${String(name || '').replace(/\s+/g, '_').toLowerCase()}`;
}

export function normalizeManualPlaces(manualData) {
  if (Array.isArray(manualData)) return manualData;
  if (manualData && Array.isArray(manualData.places)) return manualData.places;
  return [];
}

function resolveCategoryForImport(category, rules, itemName) {
  const categoryId = String(category || '').trim();
  const table = rules.getCategoryTable(categoryId);
  if (table !== 'places' && table !== 'businesses') {
    throw new Error(`Invalid import category "${category || 'missing'}" for ${itemName}`);
  }
  return { categoryId, table };
}

export function mapPriceRange(priceLevel) {
  if (priceLevel === 0 || priceLevel === 1) return '$';
  if (priceLevel === 2) return '$$';
  if (priceLevel === 3) return '$$$';
  if (priceLevel === 4) return '$$$$';
  return null;
}

function getSummaryText(summary) {
  if (!summary) return null;
  if (typeof summary === 'string') return summary;
  if (typeof summary.overview === 'string') return summary.overview;
  return null;
}

function getDescription() {
  return null;
}

function getManualDescription(place) {
  const summary = getSummaryText(place.editorial_summary);
  return summary ? summary.slice(0, 200) : null;
}

function getImageUrl(place, rules) {
  return null;
}

function formatOpenHours(openingHours) {
  if (!openingHours || !Array.isArray(openingHours.weekday_text)) return null;
  return openingHours.weekday_text.join('; ');
}

function getLatitude(place) {
  return place.lat != null ? place.lat : place.latitude;
}

function getLongitude(place) {
  return place.lng != null ? place.lng : place.longitude;
}

function explicitImportCaza(place, rules) {
  if (place?.caza == null || String(place.caza).trim() === '') return null;
  return rules?.normalizeCatalogCaza?.(place.caza) || null;
}

function resolveImportCaza(place, rules) {
  const explicit = explicitImportCaza(place, rules);
  if (explicit || (place?.caza != null && String(place.caza).trim() !== '')) return explicit;

  // This retired research feed covered Zgharta only. Keep legacy rows only when
  // their own village or coordinates prove that ownership; never infer Bsharri
  // into a pipeline whose database fallback is Zgharta.
  if (rules?.findKnownVillageCaza?.(place?.village) === 'zgharta') return 'zgharta';
  const lat = getLatitude(place);
  const lng = getLongitude(place);
  if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
    const inZgharta = rules?.isCoordinateInCatalogCaza?.(lat, lng, 'zgharta') || false;
    const inBsharri = rules?.isCoordinateInCatalogCaza?.(lat, lng, 'bsharri') || false;
    if (inZgharta && !inBsharri) return 'zgharta';
  }
  return null;
}

function getBoundaryCandidate(place, rules) {
  return {
    name: place.name,
    nameAr: place.nameAr,
    name_ar: place.name_ar,
    description: place.description,
    descriptionAr: place.descriptionAr,
    description_ar: place.description_ar,
    village: place.village,
    location: place.formatted_address,
    latitude: getLatitude(place),
    longitude: getLongitude(place),
    caza: resolveImportCaza(place, rules),
  };
}

function classifyImportBoundary(place, rules) {
  if (!rules || typeof rules.classifyBoundaryCleanupAction !== 'function') {
    return { actions: ['keep'], findings: [] };
  }
  return rules.classifyBoundaryCleanupAction(getBoundaryCandidate(place, rules));
}

function boundarySkipForImport(place, rules) {
  const boundary = classifyImportBoundary(place, rules);
  if (boundary.actions.includes('keep')) return null;

  return {
    name: place.name,
    reason: 'boundary-review-required',
    actions: boundary.actions,
    findings: boundary.findings.map(finding => finding.label),
  };
}

function shapePlaceRow(place, { featured, rules, source, manual }) {
  const caza = explicitImportCaza(place, rules);
  return {
    name: place.name,
    name_ar: null,
    description: manual ? getManualDescription(place) : getDescription(place),
    description_ar: null,
    category: place.category,
    village: place.village || 'Unknown',
    latitude: getLatitude(place),
    longitude: getLongitude(place),
    image_url: getImageUrl(place, rules),
    featured,
    open_hours: formatOpenHours(place.opening_hours),
    google_place_id: place.place_id,
    active: true,
    source,
    ...(caza ? { caza } : {}),
  };
}

function shapeBusinessRow(place, { rules, source, manual }) {
  const caza = explicitImportCaza(place, rules);
  return {
    name: place.name,
    name_ar: null,
    description: manual ? getManualDescription(place) : getDescription(place),
    description_ar: null,
    category: place.category,
    village: place.village || 'Unknown',
    latitude: getLatitude(place),
    longitude: getLongitude(place),
    image_url: getImageUrl(place, rules),
    rating: manual ? place.rating || null : place.rating,
    phone: place.formatted_phone_number || null,
    website: place.website || null,
    price_range: mapPriceRange(place.price_level),
    google_place_id: place.place_id,
    active: true,
    source,
    ...(caza ? { caza } : {}),
  };
}

function buildTopByCategory(topPlaces) {
  const topByCategory = {};
  for (const place of topPlaces) {
    if (!topByCategory[place.category]) topByCategory[place.category] = place;
  }
  return topByCategory;
}

function countByCategory(rows) {
  const counts = {};
  for (const row of rows) {
    counts[row.category] = (counts[row.category] || 0) + 1;
  }
  return counts;
}

function emptyRowCounts() {
  return {
    places: 0,
    businesses: 0,
  };
}

function appendPlannedRow(target, sourceCounts, place, rowOptions) {
  const { categoryId, table } = resolveCategoryForImport(
    place.category,
    rowOptions.rules,
    place.name || place.place_id || 'unnamed item'
  );
  const normalizedPlace = categoryId === place.category
    ? place
    : { ...place, category: categoryId };
  const row = table === 'places'
    ? shapePlaceRow(normalizedPlace, rowOptions)
    : shapeBusinessRow(normalizedPlace, rowOptions);

  assertCatalogRowGoogleLiveReferenceSafe(row, {
    source: rowOptions.source,
    itemName: normalizedPlace.name || normalizedPlace.place_id,
  });

  target[table].push(row);
  sourceCounts[table]++;
}

function filterResearchPlacesForImport(researchPlaces, rules) {
  const accepted = [];
  const skipped = [];

  for (const place of researchPlaces) {
    const boundarySkip = boundarySkipForImport(place, rules);
    if (boundarySkip) {
      skipped.push(boundarySkip);
      continue;
    }
    accepted.push(place);
  }

  return { accepted, skipped };
}

function filterPlacesById(places, placeIds) {
  if (!placeIds) {
    return {
      places,
      placeIdFilter: {
        enabled: false,
        requestedIds: [],
        matchedIds: [],
        missingIds: [],
      },
    };
  }

  const requestedSet = new Set(placeIds);
  const filtered = [];
  const matchedSet = new Set();

  for (const place of places) {
    const placeId = normalizePlaceId(place && place.place_id);
    if (!requestedSet.has(placeId)) continue;
    filtered.push(place);
    matchedSet.add(placeId);
  }

  return {
    places: filtered,
    placeIdFilter: {
      enabled: true,
      requestedIds: placeIds.slice(),
      matchedIds: placeIds.filter(id => matchedSet.has(id)),
      missingIds: placeIds.filter(id => !matchedSet.has(id)),
    },
  };
}

function filterManualPlaces(manualPlaces, researchPlaces, rules) {
  const researchNames = new Set(researchPlaces.map(place => normalizeName(place.name)).filter(Boolean));
  const seenManualNames = new Set();
  const accepted = [];
  const skipped = [];

  for (const place of manualPlaces) {
    const normalizedName = normalizeName(place && place.name);
    if (normalizedName && researchNames.has(normalizedName)) {
      skipped.push({ name: place.name, reason: 'duplicate-research-name' });
      continue;
    }
    if (normalizedName && seenManualNames.has(normalizedName)) {
      skipped.push({ name: place.name, reason: 'duplicate-manual-name' });
      continue;
    }

    const boundarySkip = boundarySkipForImport(place, rules);
    if (boundarySkip) {
      skipped.push(boundarySkip);
      continue;
    }

    if (normalizedName) seenManualNames.add(normalizedName);
    accepted.push(place);
  }

  return { accepted, skipped };
}

function batchRows(rows, batchSize) {
  const batches = [];
  for (let index = 0; index < rows.length; index += batchSize) {
    batches.push(rows.slice(index, index + batchSize));
  }
  return batches;
}

function createTagSourceOperation(table) {
  return {
    kind: 'tag-source',
    table,
    enabled: true,
    values: { source: 'manual' },
    filter: { op: 'is', column: 'source', value: null },
  };
}

function createDeactivateOperation(table, keepExisting) {
  return {
    kind: 'deactivate-existing',
    table,
    enabled: !keepExisting,
    reason: keepExisting ? 'keep-existing' : null,
    values: { active: false },
    filter: { op: 'eq', column: 'active', value: true },
  };
}

function createUpsertOperations(table, rows, batchSize) {
  return batchRows(rows, batchSize).map((batch, index) => ({
    kind: 'upsert-batch',
    table,
    enabled: true,
    batchIndex: index,
    batchSize,
    rowCount: batch.length,
    onConflict: 'google_place_id',
    rows: batch,
  }));
}

function createImportOperations({ rows, keepExisting, batchSize }) {
  const operations = [];

  for (const table of IMPORT_TABLES) operations.push(createTagSourceOperation(table));
  for (const table of IMPORT_TABLES) operations.push(createDeactivateOperation(table, keepExisting));
  for (const table of IMPORT_TABLES) {
    operations.push(...createUpsertOperations(table, rows[table], batchSize));
  }

  return operations;
}

function createRevertOperations() {
  return IMPORT_TABLES.flatMap(table => [
    {
      kind: 'activate-manual',
      table,
      enabled: true,
      values: { active: true },
      filter: { op: 'eq', column: 'source', value: 'manual' },
    },
    {
      kind: 'deactivate-non-manual',
      table,
      enabled: true,
      values: { active: false },
      filter: { op: 'neq', column: 'source', value: 'manual' },
    },
  ]);
}

function buildCategoryRouting(rules) {
  return { ...rules.categoryTables };
}

function planRevertImport({ options = {}, rules }) {
  requireImportRules(rules);
  const batchSize = normalizeBatchSize(options.batchSize);
  const keepExisting = Boolean(options.keepExisting);

  return {
    mode: 'revert',
    percentile: null,
    topCount: 0,
    totalScoredCount: 0,
    selectedResearchPlaces: [],
    skippedResearchPlaces: [],
    placeIdFilter: {
      enabled: false,
      requestedIds: [],
      matchedIds: [],
      missingIds: [],
    },
    manualPlaces: [],
    skippedManualPlaces: [],
    rows: {
      places: [],
      businesses: [],
    },
    rowCounts: {
      total: emptyRowCounts(),
      research: emptyRowCounts(),
      manual: emptyRowCounts(),
    },
    categoryCounts: {
      places: {},
      businesses: {},
    },
    categoryRouting: buildCategoryRouting(rules),
    operationPlan: {
      mode: 'revert',
      keepExisting,
      batchSize,
      operations: createRevertOperations(),
    },
  };
}

export function planResearchImport({ scoredData, manualData, options = {}, rules }) {
  requireImportRules(rules);

  if (options.revert) {
    return planRevertImport({ options, rules });
  }

  const batchSize = normalizeBatchSize(options.batchSize);
  const keepExisting = Boolean(options.keepExisting);
  const skipManual = Boolean(options.skipManual);
  const selected = selectTopScoredPlaces(scoredData, options);
  const placeIdFilter = filterPlacesById(selected.places, normalizePlaceIdFilter(options));
  const researchSelection = filterResearchPlacesForImport(placeIdFilter.places, rules);
  const topByCategory = buildTopByCategory(researchSelection.accepted);
  const manualSelection = skipManual
    ? { accepted: [], skipped: [] }
    : filterManualPlaces(normalizeManualPlaces(manualData), researchSelection.accepted, rules);
  const rows = {
    places: [],
    businesses: [],
  };
  const researchCounts = emptyRowCounts();
  const manualCounts = emptyRowCounts();

  for (const place of researchSelection.accepted) {
    const featured = topByCategory[place.category] &&
      topByCategory[place.category].place_id === place.place_id;
    appendPlannedRow(rows, researchCounts, place, {
      featured,
      manual: false,
      rules,
      source: 'google_research',
    });
  }

  for (const place of manualSelection.accepted) {
    appendPlannedRow(rows, manualCounts, place, {
      featured: false,
      manual: true,
      rules,
      source: 'manual_curated',
    });
  }

  return {
    mode: 'import',
    percentile: selected.percentile,
    topCount: selected.topCount,
    totalScoredCount: selected.totalCount,
    selectedResearchPlaces: researchSelection.accepted.slice(),
    skippedResearchPlaces: researchSelection.skipped.slice(),
    placeIdFilter: placeIdFilter.placeIdFilter,
    manualPlaces: manualSelection.accepted.slice(),
    skippedManualPlaces: manualSelection.skipped.slice(),
    rows,
    rowCounts: {
      total: {
        places: rows.places.length,
        businesses: rows.businesses.length,
      },
      research: researchCounts,
      manual: manualCounts,
    },
    categoryCounts: {
      places: countByCategory(rows.places),
      businesses: countByCategory(rows.businesses),
    },
    categoryRouting: buildCategoryRouting(rules),
    operationPlan: {
      mode: 'import',
      keepExisting,
      batchSize,
      operations: createImportOperations({ rows, keepExisting, batchSize }),
    },
  };
}

export function assertImportPlanSafeToExecute(plan) {
  if (!plan || plan.mode !== 'import') return;

  if (plan.placeIdFilter && plan.placeIdFilter.enabled && plan.placeIdFilter.missingIds.length > 0) {
    throw new Error(`Refusing to execute import with missing requested Google Place IDs: ${plan.placeIdFilter.missingIds.join(', ')}`);
  }

  const hasEnabledDeactivation = plan.operationPlan.operations.some(operation =>
    operation.enabled && operation.kind === 'deactivate-existing');

  if (hasEnabledDeactivation) {
    throw new Error('Refusing to execute destructive import plan without a transaction boundary; use --keep-existing or dry-run');
  }
}
